import { useState } from "react";
import { motion } from "framer-motion";
import { Heart, PartyPopper, Briefcase, GraduationCap, Tent, Cake, ArrowDown, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { submitCartHire } from "@/lib/api";

const OCCASIONS = [
  { icon: Heart, title: "Weddings & Engagements", desc: "A show-stopping sweet course your guests will talk about long after the last dance." },
  { icon: PartyPopper, title: "Birthdays & Private Celebrations", desc: "From garden parties to milestone birthdays — gelato makes the party." },
  { icon: Briefcase, title: "Corporate & Office Functions", desc: "Treat the team or charm your clients with a scoop of seaside nostalgia." },
  { icon: GraduationCap, title: "School Fairs & Community Events", desc: "A firm favourite with children and grown-ups alike — and a crowd-puller for your fundraiser." },
  { icon: Tent, title: "Festivals & Outdoor Functions", desc: "Built for summer — our cart is right at home in fields, marquees and harbour-side events." },
  { icon: Cake, title: "Children's Parties & Special Occasions", desc: "Ninety-plus years of making children's faces light up. Yours could be next." },
];

const INCLUDED = [
  "Our charming vintage-styled ice cream cart",
  "Your choice of our artisan gelato & vegan sorbets",
  "Served with a smile by the Morelli's team",
];

const EVENT_TYPES = [
  "Wedding / Engagement",
  "Birthday / Private celebration",
  "Corporate / Office function",
  "School fair / Community event",
  "Festival / Outdoor function",
  "Children's party",
  "Something else",
];

const inputCls = "w-full bg-transparent border-b border-teal/30 focus:border-gold focus:outline-none py-2 text-teal placeholder:text-teal/40 transition-colors";
const labelCls = "text-xs uppercase tracking-[0.25em] text-teal/60";

const CartHire = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", event_type: "", event_date: "", location: "", guests: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await submitCartHire(form);
      setSent(true);
      toast.success("Enquiry sent — we'll be in touch soon");
    } catch {
      toast.error("Could not send — please check your details and try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-cream text-teal font-body" data-testid="cart-hire-page">

      {/* ---------- Hero ---------- */}
      <section className="relative h-[85vh] min-h-[540px] overflow-hidden flex items-end" data-testid="cart-hire-hero">
        <img src="/cart-hire.webp" alt="The Morelli's ice cream cart — purple with silver bells, ready for events" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-teal-deep/90 via-teal-deep/45 to-teal-deep/20" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pb-20 w-full">
          <Reveal>
            <p className="text-xs md:text-sm uppercase tracking-[0.4em] text-gold font-medium">Cart Hire &amp; Events</p>
            <h1 className="font-display font-bold tracking-tight leading-[0.95] text-5xl md:text-7xl text-cream mt-5 [text-shadow:0_2px_14px_rgba(20,8,40,0.55)]">
              We bring the <span className="italic text-gold">parlour</span> to you.
            </h1>
            <p className="mt-6 text-cream/85 max-w-xl text-base md:text-lg leading-relaxed">
              Our ice cream cart, our artisan gelato, our team — at your wedding,
              party, fair or festival, anywhere in Kent and beyond.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-8">
            <a href="#cart-enquiry" data-testid="cart-hire-hero-cta"
              className="inline-flex items-center gap-2 rounded-full bg-gold text-teal-deep text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 hover:bg-cream transition-colors duration-300 active:scale-95">
              Enquire about your date <ArrowDown size={15} />
            </a>
          </Reveal>
        </div>
      </section>

      {/* ---------- Occasions ---------- */}
      <section className="py-24 md:py-32" data-testid="cart-hire-occasions">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <SectionHeading overline="Made For Moments" title="Perfect for Every Occasion" align="center" />
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {OCCASIONS.map((o, i) => (
              <Reveal key={o.title} delay={i * 0.08}>
                <div className="group h-full bg-white rounded-[2rem] border border-gold/25 shadow-lg shadow-teal/10 p-8 hover:-translate-y-1 hover:shadow-xl hover:shadow-teal/15 transition-all duration-300" data-testid={`occasion-${i}`}>
                  <div className="w-12 h-12 rounded-full bg-blush/60 grid place-items-center text-teal group-hover:bg-gold group-hover:text-teal-deep transition-colors duration-300">
                    <o.icon size={20} />
                  </div>
                  <h3 className="font-display font-bold text-xl text-teal mt-5">{o.title}</h3>
                  <p className="text-sm text-teal/70 leading-relaxed mt-2">{o.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- What's included ---------- */}
      <section className="py-24 md:py-28 bg-blush/20 overflow-hidden" data-testid="cart-hire-included">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <SectionHeading overline="The Full Parlour Treatment" title="What's Included" />
            <Reveal delay={0.1}>
              <ul className="mt-10 space-y-4">
                {INCLUDED.map((item) => (
                  <li key={item} className="flex items-start gap-3.5 text-base md:text-lg text-teal/85">
                    <span className="mt-1 w-5 h-5 shrink-0 rounded-full bg-gold/25 grid place-items-center text-gold"><Check size={13} strokeWidth={3} /></span>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-sm text-teal/60 leading-relaxed">
                Every event is different — tell us about yours and we'll come back with
                availability and a tailored quote. We handle set-up, serving and packing
                away, so you can simply enjoy the day.
              </p>
            </Reveal>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <Reveal className="mt-10">
              <div className="rounded-t-full rounded-b-[2rem] overflow-hidden border-4 border-gold/40 shadow-lg aspect-[3/4]">
                <img src="/knickerbocker-glory.webp" alt="A Morelli's knickerbocker glory sundae" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="rounded-t-full rounded-b-[2rem] overflow-hidden border-4 border-gold/40 shadow-lg aspect-[3/4]">
                <img src="/hero-sundae.webp" alt="A signature Morelli's sundae" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ---------- Enquiry form ---------- */}
      <section id="cart-enquiry" className="py-24 md:py-32" data-testid="cart-hire-form-section">
        <div className="max-w-2xl mx-auto px-6">
          <SectionHeading overline="Check Your Date" title="Enquire About Cart Hire" align="center" />
          <Reveal delay={0.1}>
            {sent ? (
              <div className="mt-12 bg-white rounded-[2rem] border border-gold/25 shadow-xl shadow-teal/10 p-10 text-center" data-testid="cart-hire-success">
                <p className="font-display italic text-2xl text-teal">Thank you — enquiry sent!</p>
                <p className="mt-3 text-teal/70 text-sm leading-relaxed">
                  We've got your details and will be in touch about availability and a quote.
                  In a hurry? Call us on 01843 862500.
                </p>
                <a href="/" className="mt-6 inline-flex items-center gap-2 rounded-full bg-teal text-cream text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 hover:bg-gold hover:text-teal-deep transition-colors duration-300">
                  Back to the Parlour <ArrowRight size={15} />
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-12 bg-white rounded-[2rem] border border-gold/25 shadow-xl shadow-teal/10 p-8 md:p-10 space-y-6" data-testid="cart-hire-form">
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className={labelCls} htmlFor="ch-name">Your name</label>
                    <input id="ch-name" required data-testid="cart-hire-name" value={form.name} onChange={set("name")} placeholder="Jane Smith" className={`${inputCls} mt-1`} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="ch-email">Email</label>
                    <input id="ch-email" type="email" required data-testid="cart-hire-email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={`${inputCls} mt-1`} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className={labelCls} htmlFor="ch-phone">Phone (optional)</label>
                    <input id="ch-phone" type="tel" data-testid="cart-hire-phone" value={form.phone} onChange={set("phone")} placeholder="07..." className={`${inputCls} mt-1`} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="ch-type">Type of event</label>
                    <select id="ch-type" required data-testid="cart-hire-type" value={form.event_type} onChange={set("event_type")} className={`${inputCls} mt-1`}>
                      <option value="" disabled>Choose one…</option>
                      {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className={labelCls} htmlFor="ch-date">Event date (or rough idea)</label>
                    <input id="ch-date" data-testid="cart-hire-date" value={form.event_date} onChange={set("event_date")} placeholder="e.g. 12 July 2027" className={`${inputCls} mt-1`} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="ch-guests">Roughly how many guests?</label>
                    <input id="ch-guests" data-testid="cart-hire-guests" value={form.guests} onChange={set("guests")} placeholder="e.g. 80" className={`${inputCls} mt-1`} />
                  </div>
                </div>
                <div>
                  <label className={labelCls} htmlFor="ch-location">Event location / venue</label>
                  <input id="ch-location" data-testid="cart-hire-location" value={form.location} onChange={set("location")} placeholder="e.g. Quex Park, Birchington" className={`${inputCls} mt-1`} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="ch-message">Anything else we should know?</label>
                  <textarea id="ch-message" rows={3} data-testid="cart-hire-message" value={form.message} onChange={set("message")}
                    placeholder="Timings, theme, dietary needs, favourite flavours…" className={`${inputCls} mt-1 resize-none`} />
                </div>
                <button type="submit" disabled={busy} data-testid="cart-hire-submit"
                  className="w-full rounded-full bg-teal text-cream font-semibold uppercase tracking-[0.2em] text-sm py-4 hover:bg-gold hover:text-teal-deep transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2">
                  {busy ? "Sending…" : "Send Enquiry"} <ArrowRight size={15} />
                </button>
                <p className="text-[11px] text-teal/50 text-center leading-relaxed">
                  We'll only use your details to respond to your enquiry — see our <a href="/privacy" className="underline hover:text-gold transition-colors">privacy notice</a>.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </section>

      {/* ---------- Closing CTA ---------- */}
      <section className="relative py-24 md:py-32 bg-teal-deep text-cream overflow-hidden" data-testid="cart-hire-cta">
        <img src="/parlour-night.webp" alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <motion.div whileHover={{ scale: 1.03 }} transition={{ type: "spring", stiffness: 300 }}>
              <p className="text-xs uppercase tracking-[0.4em] text-gold font-medium">Over a century of smiles</p>
              <h2 className="font-display font-bold tracking-tight leading-[0.95] text-4xl md:text-6xl mt-5">
                Let's make your day <span className="italic text-gold">unforgettable.</span>
              </h2>
            </motion.div>
            <a href="#cart-enquiry" data-testid="cart-hire-cta-enquire"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-gold text-teal-deep text-xs uppercase tracking-[0.2em] font-semibold px-8 py-4 hover:bg-cream transition-colors duration-300 active:scale-95">
              Enquire now <ArrowRight size={15} />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default CartHire;
